/**
 * Shared "draft todos" section used by both AddSegmentDialog and
 * EditSegmentDialog.
 *
 * Contract mirrors DraftPaymentsSection. See that file for the rationale
 * around the render-prop for existing rows and the multi-draft UX.
 *
 * Field grid lives in `TodoRowEditor` so the inline quick-add form in
 * ItineraryView and these draft rows stay visually identical.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";
import TodoRowEditor from "@/components/shared/TodoRowEditor";

const emptyDraft = () => ({
  description: "",
  priority: "medium",
  due_date: "",
  due_time: "",
});

const DraftTodosSection = ({
  pendingTodos,
  setPendingTodos,
  existingTodos,
  renderExistingTodo,
  defaultOpen = false,
  disabled = false,
  title = "Todos",
  // Inline-mode hooks (see DraftPaymentsSection for the full rationale).
  onAddClick,
  addFormSlot,
  addButtonLabel = "Add todo",
  addButtonTestId = "inline-todo-add-btn",
}) => {
  const hasDrafts = pendingTodos.length > 0;
  const hasExisting = Array.isArray(existingTodos) && existingTodos.length > 0;
  const [open, setOpen] = useState(defaultOpen || hasDrafts || hasExisting);

  const totalCount = pendingTodos.length + (existingTodos?.length || 0);

  const updateAt = (i, patch) => {
    const next = [...pendingTodos];
    next[i] = { ...next[i], ...patch };
    setPendingTodos(next);
  };

  return (
    <div data-testid="draft-todos-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left py-2 px-3 rounded-md hover:bg-emerald-50 dark:hover:bg-slate-800 transition-colors"
        data-testid="toggle-todos-section"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {title}
          {totalCount > 0 && (
            <span className="text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200 rounded-full px-2 py-0.5">
              {totalCount}
            </span>
          )}
        </span>
        <span className="text-xs text-gray-500 dark:text-slate-400">Optional</span>
      </button>

      {open && (
        <div className="mt-2 pl-6 space-y-3">
          {/* Existing todos (Edit dialog only) */}
          {hasExisting && (
            <div className="space-y-2">
              {existingTodos.map((t) => (
                <div key={t.id} data-testid={`existing-todo-${t.id}`}>
                  {renderExistingTodo ? renderExistingTodo(t) : null}
                </div>
              ))}
            </div>
          )}

          {/* Draft rows */}
          {pendingTodos.map((t, i) => (
            <div
              key={i}
              className="relative border border-emerald-200 dark:border-emerald-900/60 rounded-lg p-3 bg-emerald-50/40 dark:bg-emerald-950/20 space-y-2"
              data-testid={`inline-todo-row-${i}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                  New todo #{i + 1}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPendingTodos(pendingTodos.filter((_, idx) => idx !== i))}
                  disabled={disabled}
                  className="h-6 w-6 p-0 text-gray-500 hover:text-red-600"
                  data-testid={`inline-todo-remove-${i}`}
                  aria-label="Remove todo"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>

              <TodoRowEditor
                todo={t}
                onChange={(patch) => updateAt(i, patch)}
                disabled={disabled}
                testIdPrefix={`inline-todo-${i}`}
              />
            </div>
          ))}

          {/* Inline-mode add form (immediate-save context) replaces the
              "+ Add todo" button while it's active. */}
          {addFormSlot}

          {!addFormSlot && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                if (onAddClick) {
                  onAddClick();
                } else {
                  setPendingTodos([...pendingTodos, emptyDraft()]);
                }
              }}
              disabled={disabled}
              className="w-full"
              data-testid={addButtonTestId}
            >
              <Plus className="h-4 w-4 mr-1" />
              {addButtonLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default DraftTodosSection;
